import { FC, memo } from 'react';
import { FieldValues, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  Button,
  TextTitle,
  TextBody,
  TextCaption,
  Spacer,
  Stack,
  Card,
  Icon,
  IconButton,
  Textarea,
  Tooltip,
  Input,
} from '@pypestream/design-system';

import { useKnowledgeAgentUpload } from '../../../hooks';

import './step-agent.scss';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface StepAgentProps {
  nextStep: SubmitHandler<FieldValues>;
  backStep?: () => void;
  uploadedFiles: UploadedFile[];
  addFile: (file: File) => void;
  removeFile: (fileId: string) => void;
  disabled?: boolean;
}

interface FileItemProps {
  file: UploadedFile;
  testId?: string;
  onRemove: (id: string) => void;
  formatFileSize: (bytes: number) => string;
  getFileIcon: (
    fileName: string
  ) => 'file-pdf' | 'file-doc' | 'file-txt' | 'file-odt' | 'data';
}

const FileItem = memo<FileItemProps>(
  ({ testId, file, onRemove, formatFileSize, getFileIcon }: FileItemProps) => (
    <Card
      test-id={testId}
      key={file.id}
      size="small"
      shadow="none"
      className="c-create-solution-agent__file"
    >
      <Icon
        name={getFileIcon(file.name)}
        className="c-create-solution-agent__file-icon"
      />
      <TextBody>{file.name}</TextBody>
      <TextCaption>{formatFileSize(file.size)}</TextCaption>
      <IconButton
        size="small"
        name="delete"
        slot="hover-action"
        onClick={() => onRemove(file.id)}
      />
    </Card>
  )
);

FileItem.displayName = 'FileItem';

interface FileUploadSectionProps {
  uploadedFiles: UploadedFile[];
  addFile: (file: File) => void;
  removeFile: (fileId: string) => void;
}

const FileUploadSection = memo<FileUploadSectionProps>(
  ({ uploadedFiles, addFile, removeFile }: FileUploadSectionProps) => {
    const { customFileUpload, formatFileSize, getFileIcon } =
      useKnowledgeAgentUpload({
        addFile,
      });

    return (
      <>
        {customFileUpload}
        {uploadedFiles?.length > 0 && (
          <>
            <Spacer size="xsmall" />
            <div className="c-create-solution-agent__files-list">
              <Stack gutter="xsmall">
                {uploadedFiles?.map((file) => (
                  <FileItem
                    testId={`create-solution-modal-file-item-${file.id}`}
                    key={file.id}
                    file={file}
                    onRemove={removeFile}
                    formatFileSize={formatFileSize}
                    getFileIcon={getFileIcon}
                  />
                ))}
              </Stack>
            </div>
          </>
        )}
      </>
    );
  }
);

FileUploadSection.displayName = 'FileUploadSection';

export const StepAgent: FC<StepAgentProps> = ({
  nextStep,
  backStep,
  uploadedFiles,
  addFile,
  removeFile,
  disabled = false,
}) => {
  const { register, handleSubmit, watch, setValue } = useFormContext();
  const { errors } = useFormContext().formState;

  const brandName = watch('brandName');
  const isBrandNameValid = Boolean(brandName?.trim());

  return (
    <>
      <TextTitle size="small" textAlign="center">
        Create a Knowledge Agent
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        Empower your Knowledge Agent to become an expert on your content. Set a
        tone to ensure its responses align with your brand, and upload documents
        to build a comprehensive knowledge base for it to reference.
      </TextBody>
      <Spacer size="xlarge" />
      {/**
      <TextBody>Copy and paste text below</TextBody>
      <Spacer size="2xsmall" />
      <Textarea
        variant="outlined"
        rows={7}
        {...register('knowledgeAgentContext')}
        placeholder="Enter context information here..."
      />
       */}
      {errors.knowledgeAgentContext && (
        <>
          <TextBody variant="secondary" size="small">
            {errors.knowledgeAgentContext.message as string}
          </TextBody>
          <Spacer size="medium" />
        </>
      )}
      <Stack alignItems="center" gutter="2xsmall">
        <TextBody size="small">Brand Name</TextBody>
        <Tooltip
          placement="right"
          content="The name of your company or brand. This helps the model respond in a way that aligns with your brand identity."
        >
          <Icon name="info" color="gray" size="xsmall" />
        </Tooltip>
      </Stack>
      <Spacer size="2xsmall" />
      <Input
        test-id="create-solution-modal-brand-name-input"
        variant="outlined"
        placeholder="Brand Name"
        {...register('brandName', {
          required: 'Brand Name is required',
        })}
        onInput={(e) => {
          // @ts-expect-error - TODO: fix this
          const { value } = e.target;
          setValue('brandName', value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
      />
      {errors.brandName && (
        <>
          <Spacer size="xsmall" />
          <TextBody variant="secondary" size="small">
            {errors.brandName.message as string}
          </TextBody>
        </>
      )}
      <Spacer />
      <Stack alignItems="center" gutter="2xsmall">
        <TextBody size="small">Tone</TextBody>
        <Tooltip
          placement="right"
          content="Set the personality or communication style for the knowledge agent—e.g., professional, friendly, or playful."
        >
          <Icon name="info" color="gray" size="xsmall" />
        </Tooltip>
      </Stack>
      <Spacer size="2xsmall" />
      <Textarea
        variant="outlined"
        placeholder="Tone"
        rows={2}
        {...register('tone')}
      ></Textarea>
      <Spacer />
      <FileUploadSection
        uploadedFiles={uploadedFiles}
        addFile={addFile}
        removeFile={removeFile}
      />
      <Spacer size="xlarge" />
      <Stack justifyContent="space-between">
        <Button
          size="large"
          variant="ghost"
          onClick={backStep}
          disabled={disabled}
        >
          Back
        </Button>

        <Stack gutter="small" direction="row">
          <Button
            test-id="create-solution-step-agent-next-btn"
            size="large"
            variant="primary"
            onClick={handleSubmit(nextStep)}
            disabled={disabled || !isBrandNameValid}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
